import React from 'react';
import cn from 'classnames';
import { ChatUserType, GuardianChatPerson } from '@mnspro-cloud/services/lib/mnspro-cloud/api/v2';
import { ChatMessage, ChatMyMessage } from '@fluentui-contrib/react-chat';
import { formatChatMessageDate } from '../Chat.utils';
import { ChatStudentBadge } from '../ChatBadge/ChatStudentBadge';
import { ChatMessageAttachment } from '../ChatMessageAttachment/ChatMessageAttachment';
import { useChatContextOptional } from '../ChatContext';
import { Popover, PopoverSurface, PopoverTrigger } from '@fluentui/react-components';
import { TranslateAuto16Regular } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import './ChatMessageBubble.scss';
import {
  GuardianChatMessage,
  GuardianChatMessageSendState,
  MetadataAttachment,
} from '../GuardianChat.interface';

export interface ChatMessageBubble {
  message: GuardianChatMessage;
  ward?: GuardianChatPerson;
  isSelf?: boolean;
  status?: GuardianChatMessageSendState;
}

const parseAttachments = (metadata?: { [key: string]: string } | null): MetadataAttachment[] => {
  const raw = metadata?._attachments;
  if (!raw) return [];

  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

export const ChatMessageBubble: React.FC<ChatMessageBubble> = ({
  message,
  ward,
  isSelf,
  status,
}) => {
  const { t } = useTranslation();
  const context = useChatContextOptional();
  const attachments = parseAttachments(message.metadata);

  const messageContent = (
    <div>
      {attachments.map((attachment) => (
        <ChatMessageAttachment
          key={attachment.id}
          filename={attachment.fileName}
          onClick={() => context?.downloadAttachment?.(attachment.id, attachment.fileName)}
        />
      ))}
      {message.content}
    </div>
  );

  const inner = isSelf ? (
    <ChatMyMessage
      details={{
        className: cn('detailsWrapper', 'sentOn'),
        children: formatChatMessageDate(message.createdOn),
      }}
      status={status}
    >
      <div>{messageContent}</div>
    </ChatMyMessage>
  ) : (
    <ChatMessage
      details={{
        className: cn('detailsWrapper', 'withSender'),
        children: (
          <>
            <div className="sentOn">{formatChatMessageDate(message.createdOn)}</div>
            {message.sender?.chatUserType === ChatUserType.parent && ward && (
              <div className="studentBadge">
                <ChatStudentBadge userId={ward.id} displayName={ward.displayName} />
              </div>
            )}
            <div className="senderName">{message.sender?.displayName}</div>
          </>
        ),
      }}
    >
      <div>
        {messageContent}
        {message.metadata?.original_message && (
          <Popover withArrow>
            <PopoverTrigger disableButtonEnhancement>
              <div
                className="flex items-center gap-1"
                style={{ color: 'var(--colorNeutralForeground3)', cursor: 'pointer', marginTop: 4 }}
              >
                <TranslateAuto16Regular />
                <span style={{ fontSize: 12 }}>
                  {t('pc:chat:chatMessageBubble:auto_translated', {
                    defaultValue: 'Diese Nachricht wurde automatisch übersetzt. Original anzeigen.',
                  })}
                </span>
              </div>
            </PopoverTrigger>
            <PopoverSurface>{message.metadata.original_message}</PopoverSurface>
          </Popover>
        )}
      </div>
    </ChatMessage>
  );

  return <div className="ChatMessageBubble">{inner}</div>;
};
